import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, catchError, finalize } from 'rxjs';
import { GlobalService } from 'src/app/_services/global.service';
import { AutoUnsubscribe } from 'src/app/shared/decorators/auto-unsubscribe.decorator';
import { ErrorMsgService } from 'src/app/shared/error-msg/error-msg.service';
import { BusBulkEnrollService } from '../../_services/bus-bulk-enroll.service';
import * as XLSX from 'xlsx';
import * as FILESAVER from 'file-saver';
import { BusStudentsService } from '../../_services/bus-students.service';
declare var $: any;
type SkipType = 'single' | 'multiple';


@AutoUnsubscribe
@Component({
  selector: 'app-validation-errors',
  templateUrl: './validation-errors.component.html',
  styleUrls: ['./validation-errors.component.scss']
})
export class ValidationErrorsComponent {
  @Input() lang: 'ar' | 'en' = 'en';
  @Input() validationErrors: any[] = [];
  @Input() batch_id: string;
  
  destroy$ = new Subject<void>();
  progress: number = 0;
  completedErrors: number = 0;
  originalErrorsLength: number;
  loading = false;
  upload_state: string = 'errors-fixed';
  uploaded_data: any = { num_enrolled_students: 0, num_not_enrolled_students: 0 };
  enrollDetails: any;
  successStudents: any[] = [];
  successStudentsIds: any[] = [];
  failStudents: any[] = [];
  failStudentsIds: any[] = [];
  show_details_popup = false;
  activeTab: 'success' | 'failed' = 'success';
  constructor(
    private router: Router,
    private busBulkEnrollService: BusBulkEnrollService,
    private errorMsgService: ErrorMsgService,
    private globalService: GlobalService,
    private busService: BusStudentsService
  ) {}

  ngOnInit(): void {
    $('.modal').modal('hide');
    $('body').removeClass('modal-open');
    $('.modal-backdrop').remove();
    this.globalService.getNotifyViewWithBulkEnrollState().pipe(takeUntil(this.destroy$)).subscribe(res => {
      if(res == 'finshed-with-details'){
        this.upload_state = res;
      }
      else if(res == 'enroll-in-progress'){
        this.upload_state = res;
      }
      else {
        this.upload_state = 'errors-fixed';
      }
    })
    this.busService.getEnrollDetails().pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.uploaded_data.num_enrolled_students = res?.success_count;
      this.uploaded_data.num_not_enrolled_students = res?.fail_count + res?.ignored_count;
      this.enrollDetails = res;
      this.successStudentsIds = res?.subscribed_ids;
      this.failStudentsIds = [...res?.failed_ids, ...res?.ignored_ids];
    })
    this.globalService.getAutoBusEnroll().pipe(takeUntil(this.destroy$)).subscribe(res => {
      if(res){
        this.enroll();
      }
    })
    this.initializeComponent();
  }

  private initializeComponent(): void {
    const firstError = this.validationErrors[0];
    if (firstError) {
      firstError.selected = true;
    }
    this.originalErrorsLength = this.validationErrors?.length || 0;
  }

  downloadErrors(): void {
    if (!this.validationErrors || this.validationErrors.length === 0) {
      return;
    }

    // Create workbook and worksheet
    const workBook = XLSX.utils.book_new();
    const sheetName = 'Enroll Errors';
    workBook.SheetNames.push(sheetName);

    // Prepare data for export - only student_id and trip_id
    const exportData = this.validationErrors.map(error => ({
      student_id: error.student_id,
      trip_id: error.trip_id
    }));

    // Convert data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);

    // Add worksheet to workbook
    workBook.Sheets[sheetName] = worksheet;

    // Generate Excel file
    const excelBuffer = XLSX.write(workBook, { bookType: 'xlsx', type: 'array' });

    // Create blob and download
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const fileName = `enroll_errors_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    FILESAVER.saveAs(blob, fileName);
  }

  progressLocalization(progress?: number): string {
    const locale = this.lang === "en" ? 'en-US' : 'ar-EG';
    return progress?.toLocaleString(locale) || '0';
  }

  selectError(error: any, saveNext: boolean): void {
    this.deselectAllErrors();
    
    const errorIndex = this.validationErrors.indexOf(error);
    const adjustedIndex = this.getAdjustedIndex(errorIndex);
    
    error.selected = true;
    
    if (saveNext) {
      this.loading = true;
      this.updateFailedRecord(error, adjustedIndex);
    }
  }

  private deselectAllErrors(): void {
    this.validationErrors.forEach((err: any) => {
      err.selected = false;
    });
  }

  private getAdjustedIndex(currentIndex: number): number {
    // If this is the last error, return -1 so next becomes 0
    return currentIndex === this.validationErrors.length - 1 ? -1 : currentIndex;
  }

  skipErrors(type: SkipType, error?: any, index?: number): void {
    this.loading = true;
    
    const payload = this.buildSkipPayload(type, error);
    
    this.busBulkEnrollService.ignoreRecords(
      payload,
      { batch_id: this.batch_id }
    ).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        return [];
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe(res => {
      this.handleSkipSuccess(type, error, index);
    });
  }

  private buildSkipPayload(type: SkipType, error?: any): { record_ids: string[] } {
    switch (type) {
      case 'single':
        return { record_ids: error ? [error.id] : [] };
      case 'multiple':
        return { record_ids: this.validationErrors.map((err: any) => err.id) };
      default:
        return { record_ids: [] };
    }
  }

  private handleSkipSuccess(type: SkipType, error?: any, index?: number): void {
    switch (type) {
      case 'single':
        if (error && typeof index === 'number') {
          this.skipError(error, index);
        }
        break;
      case 'multiple':
        this.validationErrors = [];
        break;
    }
  }

  private skipError(error: any, index: number): void {
    this.validationErrors = this.validationErrors.filter((err: any) => err !== error);
    this.updateProgress();
    this.selectNextError(index);
  }

  private updateProgress(): void {
    this.completedErrors++;
    this.progress = this.calculateProgress();
  }

  private calculateProgress(): number {
    if (this.originalErrorsLength === 0) return 0;
    return Math.round(((this.completedErrors / this.originalErrorsLength) * 100) * 10) / 10;
  }

  private selectNextError(currentIndex: number): void {
    if (this.validationErrors.length === 0) return;
    
    const nextIndex = currentIndex >= this.validationErrors.length ? 0 : currentIndex;
    this.validationErrors[nextIndex].selected = true;
  }

  enroll(): void {
    this.globalService.setNotifyViewWithBulkEnrollState('enroll-in-progress');
    this.globalService.setBulkEnrollNotificationState('enroll-in-progress');
    this.busBulkEnrollService.enrollValidatedStudents(
      null,
      { batch_id: this.batch_id },
      {},
    ).pipe(
      takeUntil(this.destroy$),
    ) .subscribe(res => {
      if(res?.success){
      }
      else {
        this.loading = false;
        this.errorMsgService.setMessage(res?.message);
      }
    },(err) => {
      this.errHandler(err);
    })
  }

  updateFailedRecord(error: any, index: number): void {
    const payload = this.buildUpdatePayload(error);
    
    this.busBulkEnrollService.updateFailedRecord(
      payload,
      { batch_id: this.batch_id }
    ).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        return [];
      }),
      finalize(() => {
        this.loading = false;
      })
    ).subscribe((res: any) => {
      if (res.success) {
        this.handleUpdateSuccess(error, index);
      } else {
        // Always update the error with the latest data from the response
        const errIdx = this.validationErrors.findIndex((e: any) => e.id === res.data.id);
        if (errIdx !== -1) {
          this.validationErrors[errIdx] = {
            ...this.validationErrors[errIdx],
            ...res?.data,
            original_student_id: res?.data?.student_id
          };
        }
      }
    });
  }

  private buildUpdatePayload(error: any): { id: string; student_id: string; trip_id: string } {
    return {
      id: error?.id,
      student_id: error?.student_id,
      trip_id: error?.trip_id
    };
  }

  private handleUpdateSuccess(error: any, index: number): void {
    // Select next error
    if (this.validationErrors[index + 1]) {
      this.validationErrors[index + 1].selected = true;
    }
    
    // Update progress
    this.completedErrors++;
    this.progress = this.calculateProgress();
    
    // Remove the processed error
    this.validationErrors = this.validationErrors.filter((err: any) => err !== error);
  }

  clearFieldError(error: any, field: string) {
    if (error?.validation_errors && error.validation_errors[field]) {
      error.validation_errors[field] = null;
    }
  }

  ngOnDestroy(): void {
    this.closeDetailsPopup();
    // AutoUnsubscribe decorator handles cleanup
  }

  showDetailsPopup(){
    this.loading = true;
    let body: any = {}
    body.ids = this.successStudentsIds;
    if(this.successStudentsIds?.length > 0){
      this.busService.getStudentsByIds(body).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        this.successStudents = res;
        this.show_details_popup = true;
        this.loading = false;
      },(err) => {
        this.loading = false;
        this.show_details_popup = false;
        this.errHandler(err);
      })

    }
    else {
      this.show_details_popup = true;
      this.loading = false;
    }
  }

  // filterFailAndSuccess(allStudents) {
  //   if(allStudents?.length > 0){
  //     this.successStudents = allStudents.filter(student =>
  //       this.enrollDetails?.subscribed_ids.includes(student?.id?.toString())
  //     );
  //     const failedAndIgnoredIds = [
  //       ...(this.enrollDetails?.failed_ids || []),
  //       ...(this.enrollDetails?.ignored_ids || [])
  //     ];
  
  //     this.failStudents = allStudents.filter(student =>
  //       failedAndIgnoredIds.includes(student?.id?.toString())
  //     );
  //     this.show_details_popup = true;
  //   }
  // }

  closeDetailsPopup(){
    this.show_details_popup = false;
  }

  handleBottomButtonAction(): void {
    // Close the popup and continue with the flow
    this.closeDetailsPopup();
    this.globalService.setBulkEnrollNotificationState('not-uploading');
    this.globalService.setBusEnrollNotificationState('');
    this.router.navigateByUrl('/buses/bulk-enroll/assign-trips');
  }

  errHandler(err: any){
    this.loading = false;
    if(err?.error?.error_type){
      if(err?.error?.error_type == 'all_records_ignored'){
        this.globalService.setNotifyViewWithBulkEnrollState('finshed-with-details');
        this.globalService.setBulkEnrollNotificationState('not-uploading');
        this.globalService.setAutoBusEnroll(false);
        this.busService.setEnrollDetails(err?.error?.data);
      }
    }
    else {
      this.errorMsgService.setMessage(err?.error?.message);
    }
  }

  goBack(e) {
    e.stopPropagation();
    this.globalService.setBulkEnrollNotificationState('not-uploading');
    this.router.navigateByUrl('/buses/students');
  }
}
